import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { ClauseAnalysis } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

export interface DocumentViewerRef {
    scrollToClause: (clauseIndex: number) => void;
}

interface DocumentViewerProps {
    fileName: string;
    content?: string | string[]; // HTML string or paragraphs array
    htmlContent?: string; // Pre-rendered HTML content
    clauses?: ClauseAnalysis[];
    selectedClauseIndex?: number | null;
    onSelectClause?: (index: number | null) => void;
    isAnalyzing?: boolean;
    analysisProgress?: number;
    onDownload?: () => void;
    showDownload?: boolean; // 是否显示下载按钮（分析完成后才显示）
}

const A4_WIDTH_PX = 794;

export const DocumentViewer = forwardRef<DocumentViewerRef, DocumentViewerProps>(({
    fileName,
    content,
    htmlContent,
    clauses = [],
    selectedClauseIndex,
    onSelectClause,
    isAnalyzing = false,
    analysisProgress = 0,
    onDownload,
    showDownload = false
}, ref) => {
    const { t } = useLanguage();
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
        scrollToClause: (clauseIndex: number) => {
            if (!contentRef.current) return;
            const selectors = [
                `[data-clause-index="${clauseIndex}"]`,
                `p[data-clause-index="${clauseIndex}"]`,
                `.risk-high[data-clause-index="${clauseIndex}"]`,
                `.risk-medium[data-clause-index="${clauseIndex}"]`,
                `.risk-critical[data-clause-index="${clauseIndex}"]`
            ];

            for (const selector of selectors) {
                const element = contentRef.current.querySelector(selector);
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });
                    return;
                }
            }

            const allClauses = contentRef.current.querySelectorAll('[data-clause-index]');
            if (allClauses.length > 0 && clauseIndex < allClauses.length) {
                allClauses[clauseIndex]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            }
        }
    }));

    const displayHtml = React.useMemo(() => {
        if (htmlContent) {
            if (clauses.length > 0) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = htmlContent;
                const allParagraphs = tempDiv.querySelectorAll('p');

                clauses.forEach((clause, clauseIndex) => {
                    if (!clause.originalText || !clause.originalText.trim()) return;

                    const clauseText = clause.originalText.trim();
                    let matched = false;

                    allParagraphs.forEach((pElement) => {
                        if (pElement.hasAttribute('data-clause-index')) return;

                        const paragraphText = pElement.textContent || pElement.innerText || '';
                        const cleanParagraphText = paragraphText.trim();

                        const matchStrategies = [
                            () => cleanParagraphText === clauseText,
                            () => cleanParagraphText.includes(clauseText),
                            () => clauseText.includes(cleanParagraphText) && cleanParagraphText.length > 20,
                            () => {
                                const clausePrefix = clauseText.substring(0, Math.min(50, clauseText.length));
                                const paraPrefix = cleanParagraphText.substring(0, Math.min(50, cleanParagraphText.length));
                                return paraPrefix === clausePrefix && clausePrefix.length > 20;
                            }
                        ];

                        for (const strategy of matchStrategies) {
                            if (strategy()) {
                                matched = true;
                                const riskClass = `risk-${clause.riskLevel.toLowerCase()}`;
                                const highlightClass = clauseIndex === selectedClauseIndex ? 'highlighted-clause' : '';

                                const existingClass = pElement.getAttribute('class') || '';
                                const newClass = `${existingClass} ${riskClass} ${highlightClass}`.trim();
                                pElement.setAttribute('class', newClass);
                                pElement.setAttribute('data-clause-index', clauseIndex.toString());

                                if (clause.riskDescription || clause.suggestedAction) {
                                    const analysisDiv = document.createElement('div');
                                    analysisDiv.className = 'revision-suggestion';
                                    analysisDiv.style.cssText = 'margin-top: 12px; margin-bottom: 20px; padding: 16px; background-color: rgba(59, 130, 246, 0.08); border-left: 4px solid #3b82f6; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);';

                                    if (clause.riskDescription) {
                                        const riskTitleP = document.createElement('p');
                                        riskTitleP.style.cssText = 'font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;';
                                        riskTitleP.textContent = t('risk.analysis') + '：';

                                        const riskContentP = document.createElement('div');
                                        riskContentP.style.cssText = 'font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0 0 12px 0; white-space: pre-wrap;';
                                        riskContentP.textContent = clause.riskDescription.replace(/\\n/g, '\n');

                                        analysisDiv.appendChild(riskTitleP);
                                        analysisDiv.appendChild(riskContentP);
                                    }

                                    if (clause.suggestedAction) {
                                        const suggestionTitleP = document.createElement('p');
                                        suggestionTitleP.style.cssText = 'font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;';
                                        suggestionTitleP.textContent = t('risk.suggestion') + '：';

                                        const suggestionContentP = document.createElement('div');
                                        suggestionContentP.style.cssText = 'font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0; white-space: pre-wrap;';
                                        suggestionContentP.textContent = clause.suggestedAction.replace(/\\n/g, '\n');

                                        analysisDiv.appendChild(suggestionTitleP);
                                        analysisDiv.appendChild(suggestionContentP);
                                    }

                                    pElement.parentNode?.insertBefore(analysisDiv, pElement.nextSibling);
                                }
                                return;
                            }
                        }
                    });
                });
                return tempDiv.innerHTML;
            }
            return htmlContent;
        }

        if (typeof content === 'string') return content;

        if (Array.isArray(content) && content.length > 0) {
            return content.map((paragraph, index) => {
                const paragraphText = paragraph.trim();
                const clause = clauses.find(c => {
                    const clauseText = c.originalText.trim();
                    return clauseText === paragraphText || paragraphText.includes(clauseText.substring(0, 30));
                });

                const clauseIndex = clause ? clauses.indexOf(clause) : null;
                const dataAttr = clauseIndex !== null ? `data-clause-index="${clauseIndex}"` : '';
                const riskClass = clause ? `risk-${clause.riskLevel.toLowerCase()}` : '';
                const highlightClass = clauseIndex === selectedClauseIndex ? 'highlighted-clause' : '';

                let paragraphHtml = `<p ${dataAttr} class="${riskClass} ${highlightClass}">${paragraph}</p>`;

                if (clause && (clause.riskDescription || clause.suggestedAction)) {
                    let analysisHtml = `<div class="revision-suggestion" style="margin-top: 12px; margin-bottom: 20px; padding: 16px; background-color: rgba(59, 130, 246, 0.08); border-left: 4px solid #3b82f6; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">`;

                    if (clause.riskDescription) {
                        const formattedRisk = (clause.riskDescription || '').replace(/\\n/g, '\n').replace(/\n/g, '<br>');
                        analysisHtml += `<p style="font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;">${t('risk.analysis')}：</p>`;
                        analysisHtml += `<div style="font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0 0 12px 0; white-space: pre-wrap;">${formattedRisk}</div>`;
                    }

                    if (clause.suggestedAction) {
                        const formattedSuggestion = (clause.suggestedAction || '').replace(/\\n/g, '\n').replace(/\n/g, '<br>');
                        analysisHtml += `<p style="font-size: 11pt; color: #1e40af; font-weight: bold; margin: 0 0 8px 0;">${t('risk.suggestion')}：</p>`;
                        analysisHtml += `<div style="font-size: 10pt; color: #1e293b; line-height: 1.6; margin: 0; white-space: pre-wrap;">${formattedSuggestion}</div>`;
                    }

                    analysisHtml += `</div>`;
                    paragraphHtml += analysisHtml;
                }
                return paragraphHtml;
            }).join('');
        }
        return '';
    }, [content, htmlContent, clauses, selectedClauseIndex, t]);

    return (
        <div
            ref={containerRef}
            className="flex-1 overflow-hidden flex flex-col items-center relative h-full"
            style={{
                backgroundColor: 'transparent',
                paddingTop: '100px',
                paddingBottom: '64px',
                paddingLeft: '40px',
                paddingRight: '16px',
                height: '100%'
            }}
        >
            <div
                className="relative rounded-2xl overflow-hidden flex flex-col"
                style={{
                    width: '100%',
                    maxWidth: `${A4_WIDTH_PX}px`,
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
                    backdropFilter: 'blur(40px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                    border: '1px solid rgba(255, 255, 255, 0.8)'
                }}
            >
                {isAnalyzing && (
                    <div
                        className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full flex items-center gap-2"
                        style={{
                            backgroundColor: 'rgba(34, 197, 94, 0.15)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.2)'
                        }}
                    >
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-semibold text-green-700">
                            {t('risk.analyzing')}
                        </span>
                    </div>
                )}
                <div
                    className="flex-shrink-0 flex items-center justify-between"
                    style={{
                        backgroundColor: '#FFFFFF',
                        padding: '12px 32px',
                        borderBottom: '1px solid #E5E5E5'
                    }}
                >
                    <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b', margin: 0, fontFamily: '"Inter", sans-serif' }}>
                        {fileName}
                    </h1>
                    {onDownload && showDownload && (
                        <button
                            onClick={onDownload}
                            className="px-4 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                            style={{ border: '1px solid rgba(37, 99, 235, 0.2)' }}
                        >
                            {t('nav.download')}
                        </button>
                    )}
                </div>

                <div
                    ref={contentRef}
                    className="flex-1 overflow-y-auto custom-scrollbar"
                    style={{ padding: '32px', minHeight: 0 }}
                >
                    {!displayHtml && isAnalyzing ? (
                        <div className="flex items-center justify-center h-full p-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-slate-500 text-sm">{t('wb.loading')}</p>
                                {analysisProgress > 0 && <p className="text-slate-400 text-xs mt-2">{analysisProgress}%</p>}
                            </div>
                        </div>
                    ) : !displayHtml ? (
                        <div className="flex items-center justify-center h-full p-12">
                            <div className="text-center max-w-md">
                                <svg className="w-20 h-20 text-slate-300 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <h3 className="text-slate-600 text-base font-semibold mb-2">{t('wb.error')}</h3>
                                <p className="text-slate-400 text-sm mb-4 leading-relaxed">{t('wb.errorDesc')}</p>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="relative document-content"
                            style={{ width: '100%', backgroundColor: '#FFFFFF', fontFamily: '"Inter", sans-serif', fontSize: '11pt', lineHeight: 1.5, color: '#000000', padding: '48px 60px' }}
                            dangerouslySetInnerHTML={{ __html: displayHtml }}
                            onClick={(e) => {
                                const target = e.target as HTMLElement;
                                const paragraph = target.closest('p[data-clause-index]');
                                if (paragraph && onSelectClause) {
                                    const index = parseInt(paragraph.getAttribute('data-clause-index') || '-1');
                                    if (index >= 0) onSelectClause(index);
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <style>{`
                .document-content p { margin: 0 0 0.5em 0; }
                .risk-critical { background: linear-gradient(90deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.03) 100%); border-left: 4px solid #dc2626; padding: 8px 12px 8px 16px; margin: 4px 0; border-radius: 0 6px 6px 0; }
                .risk-high { background: linear-gradient(90deg, rgba(249, 115, 22, 0.08) 0%, rgba(249, 115, 22, 0.03) 100%); border-left: 4px solid #f97316; padding: 8px 12px 8px 16px; margin: 4px 0; border-radius: 0 6px 6px 0; }
                .risk-medium { background: linear-gradient(90deg, rgba(234, 179, 8, 0.08) 0%, rgba(234, 179, 8, 0.03) 100%); border-left: 4px solid #eab308; padding: 8px 12px 8px 16px; margin: 4px 0; border-radius: 0 6px 6px 0; }
                .risk-low { background: linear-gradient(90deg, rgba(34, 197, 94, 0.06) 0%, rgba(34, 197, 94, 0.02) 100%); border-left: 4px solid #22c55e; padding: 8px 12px 8px 16px; margin: 4px 0; border-radius: 0 6px 6px 0; }
                .highlighted-clause { background-color: rgba(59, 130, 246, 0.15) !important; border-left-color: #3b82f6 !important; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: rgba(0, 0, 0, 0.05); border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.1); border-radius: 10px; }
            `}</style>
        </div>
    );
});

DocumentViewer.displayName = 'DocumentViewer';
